import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { IconStamp, IconSend, IconPaw } from "@/business/contract/icons";
import { useSealList } from "~/services/business/seal";
import {
  useContractTemplateDetail,
  useContractTemplateList,
} from "~/services/business/contractTemplate";
import { useSendContractRequest } from "~/services/business/contractRequest";
import {
  searchContractRecipients,
  resolveMemberCode,
  type ContractRecipientRow,
} from "~/services/business/myPage";
import QrScannerModal from "@/common/QrScannerModal";

export const Route = createFileRoute("/business/layout/contract-request-send")({
  component: RouteComponent,
  // 이용권 발급 화면의 '미계약 펫' 팝업에서 넘어올 때 수신자·펫을 미리 선택하기 위한 파라미터
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    userSeq?: number;
    petSeq?: number;
    userName?: string;
    petName?: string;
  } => ({
    userSeq: search.userSeq ? Number(search.userSeq) : undefined,
    petSeq: search.petSeq ? Number(search.petSeq) : undefined,
    userName: search.userName ? String(search.userName) : undefined,
    petName: search.petName ? String(search.petName) : undefined,
  }),
});

// ──────────────────────────────────────────────────────────

const manifestHasSealField = (placementManifest?: string | null): boolean => {
  if (!placementManifest) return false;
  try {
    const parsed = JSON.parse(placementManifest);
    return (
      Array.isArray(parsed?.items) &&
      parsed.items.some(
        (item: any) => item?.kind === "field" && item?.fieldType === "seal",
      )
    );
  } catch {
    return false;
  }
};

const templateSealSeq = (
  fields?: { cdFieldType?: string; defaultValue?: string | null }[] | null,
) => {
  const raw = fields?.find(
    (field) => field.cdFieldType === "FIELD_TYPE_SEAL",
  )?.defaultValue;
  if (!raw || !raw.trim()) return null;
  const seq = Number(raw);
  return Number.isFinite(seq) ? seq : null;
};

function RouteComponent() {
  const { history } = useRouter();

  // 단계: 1) 템플릿  2) 받는이  3) 메시지(선택) — "미리 채울 변수" 단계 제거됨
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // 1. 템플릿 선택 (API) — 활성 양식만 발송 후보
  const { data: allTemplates = [] } = useContractTemplateList();
  const templates = useMemo(
    () =>
      allTemplates.filter(
        (t) => t.cdContractStatus === "CONTRACT_STATUS_ACTIVE",
      ),
    [allTemplates],
  );
  const [templateSeq, setTemplateSeq] = useState<number | null>(null);
  const template = useMemo(
    () => templates.find((t) => t.contractSeq === templateSeq) ?? null,
    [templates, templateSeq],
  );
  const { data: templateDetail, isFetching: isTemplateDetailFetching } =
    useContractTemplateDetail(templateSeq ?? undefined);
  const templateRequiresSeal = useMemo(
    () =>
      !!templateDetail?.fields?.some(
        (field) => field.cdFieldType === "FIELD_TYPE_SEAL",
      ) || manifestHasSealField(templateDetail?.placementManifest),
    [templateDetail],
  );
  const configuredSealSeq = useMemo(
    () => templateSealSeq(templateDetail?.fields),
    [templateDetail?.fields],
  );
  const sealRequirementKnown = !template || !!templateDetail;

  // 2. 받는 사람 선택 — 이 사업장 고객(예약이력)으로 통합검색.
  //    발급 화면과 동일: 회원코드·전화·펫이름 한 입력창 + QR. 계약완료/미계약 뱃지, 마스킹.
  //    (계약을 '받으려고' 보내는 화면이라 계약 게이트 없음. 이용권 보유 여부와 무관)
  const [searchInput, setSearchInput] = useState("");
  const [results, setResults] = useState<ContractRecipientRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedUserSeq, setSelectedUserSeq] = useState<number | null>(null);
  const [selectedPetSeq, setSelectedPetSeq] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedPetName, setSelectedPetName] = useState("");

  const CODE_RE = /^[2-9A-HJ-NP-Z]{8}$/;
  const extractCode = (raw: string): string => {
    const t = raw.trim().toUpperCase();
    if (t.startsWith("MTKT:")) return (t.split(":")[1] ?? "").trim();
    if (CODE_RE.test(t)) return t;
    return "";
  };

  // 통합 검색 — 코드/QR → 회원조회(전 펫), 숫자 → 전화번호, 그 외 → 펫이름
  const runRecipientSearch = async (raw: string) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return;
    setSearchError("");
    setResults([]);
    setSearchLoading(true);
    try {
      const code = extractCode(trimmed);
      if (code) {
        const member = await resolveMemberCode(code);
        if (!member || member.pets.length === 0) {
          setSearchError("없는 코드입니다.");
          return;
        }
        setResults(
          member.pets.map((p) => ({
            userSeq: member.userSeq,
            userName: member.userName,
            phoneNumber: member.phoneNumber,
            petSeq: p.petSeq,
            petName: p.petName,
            existingCustomer: p.existingCustomer, // 계약완료 여부
          })),
        );
        return;
      }
      const isPhone = /^[0-9\-\s]+$/.test(trimmed);
      const rows = await searchContractRecipients(
        isPhone ? "PHONENUMBER" : "PETNAME",
        isPhone ? trimmed.replace(/[^0-9]/g, "") : trimmed,
      );
      setResults(rows);
      if (rows.length === 0) setSearchError("검색 결과가 없습니다.");
    } catch {
      setSearchError("없는 코드입니다.");
    } finally {
      setSearchLoading(false);
    }
  };

  const selectRecipient = (row: ContractRecipientRow) => {
    setSelectedUserSeq(row.userSeq);
    setSelectedPetSeq(row.petSeq);
    setSelectedUserName(row.userName);
    setSelectedPetName(row.petName);
    setStep(3);
  };

  // 이용권 발급 '미계약 펫' 팝업에서 넘어온 경우 — 수신자·펫을 미리 선택(재검색 불필요).
  // 템플릿은 먼저 골라야 하므로 step 은 1(템플릿)로 두고, 받는사람 카드에 선택 상태만 미리 채운다.
  const prefill = Route.useSearch();
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (prefill.userSeq && prefill.petSeq) {
      prefilledRef.current = true;
      setSelectedUserSeq(prefill.userSeq);
      setSelectedPetSeq(prefill.petSeq);
      setSelectedUserName(prefill.userName ?? "");
      setSelectedPetName(prefill.petName ?? "");
    }
  }, [prefill]);

  // 메시지
  const [message, setMessage] = useState("");

  // 도장 — API (발송 시 PIN 미요구: 도장 사용 승인은 사업자가 계약서 '활성화' 시 이미 완료)
  const { data: sealList = [] } = useSealList();
  const activeSeals = useMemo(
    () => sealList.filter((s) => s.activeYn === "Y" && !!s.imageUrl),
    [sealList],
  );
  const defaultSealSeq =
    activeSeals.find((s) => s.defaultYn === "Y")?.sealSeq ?? null;
  const [selectedSealSeq, setSelectedSealSeq] = useState<number | null>(null);
  useEffect(() => {
    setSelectedSealSeq(null);
  }, [templateSeq]);
  useEffect(() => {
    if (
      selectedSealSeq != null &&
      !activeSeals.some((s) => s.sealSeq === selectedSealSeq)
    ) {
      setSelectedSealSeq(null);
      return;
    }
    const configuredSealIsUsable =
      configuredSealSeq != null &&
      activeSeals.some((s) => s.sealSeq === configuredSealSeq);
    // 우선순위: 계약서에 지정된 도장 → 회사 기본도장 → (둘 다 없으면) 활성 도장 첫 번째.
    // 현 스펙상 SEAL 필드엔 특정 도장이 바인딩돼 있지 않으므로, 회사에 활성 도장이 있으면
    // 자동 선택해 매번 재선택하는 번거로움을 없앤다. 실제 승인은 이후 도장 PIN 단계에서 이뤄진다.
    const preferredSealSeq = configuredSealIsUsable
      ? configuredSealSeq
      : (defaultSealSeq ??
        (activeSeals.length > 0 ? activeSeals[0].sealSeq : null));
    if (selectedSealSeq == null && preferredSealSeq != null) {
      setSelectedSealSeq(preferredSealSeq);
    }
  }, [activeSeals, configuredSealSeq, defaultSealSeq, selectedSealSeq]);
  const selectedSeal = activeSeals.find((s) => s.sealSeq === selectedSealSeq);

  const sendMutation = useSendContractRequest();
  const canSend =
    !!template &&
    !!selectedUserSeq &&
    sealRequirementKnown &&
    (!templateRequiresSeal || !!selectedSealSeq);

  /** 발송 시도. 성공 true / 실패 false 반환. */
  const doSend = async (): Promise<boolean> => {
    if (!template || !selectedUserSeq) return false;
    try {
      await sendMutation.mutateAsync({
        contractSeq: template.contractSeq,
        userSeq: selectedUserSeq,
        petSeq: selectedPetSeq ?? undefined,
        sealSeq: selectedSealSeq ?? undefined,
        pushMessage: message || undefined,
      });
      alert(
        `${selectedUserName}${
          selectedPetName ? `(${selectedPetName})` : ""
        }님에게 "${template.contractName}" 서명 요청을 보냈어요!` +
          (selectedSeal ? `\n사업장 도장: ${selectedSeal.sealName}` : ""),
      );
      // 발송 폼 entry pop — 한번에 이전(현황)으로
      if (window.history.length > 1) history.back();
      else history.replace("/business/layout/contract-status" as any);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const handleSend = () => {
    if (!template || !selectedUserSeq) return;
    if (!sealRequirementKnown || isTemplateDetailFetching) {
      alert("계약서 정보를 불러오는 중입니다.");
      return;
    }
    if (templateRequiresSeal && !selectedSealSeq) {
      alert("사업장 도장을 선택해 주세요.");
      return;
    }
    // 발송은 순수 dispatch — 도장은 보호자 서명 완료 시 합성되고, 도장 사용 승인은
    // 사업자가 계약서 '활성화' 시 PIN 으로 이미 완료했으므로 발송 시점엔 PIN 을 요구하지 않는다.
    // (선생님 계정도 매장 사업자의 활성 계약서를 그대로 발송할 수 있다.)
    doSend().then((ok) => {
      if (!ok) alert("발송에 실패했어요. 잠시 후 다시 시도해 주세요.");
    });
  };

  return (
    <>
      <LayoutHeader title={"새 서명 요청"} hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        {/* ── 1. 템플릿 선택 ── */}
        <Section
          step={1}
          currentStep={step}
          title="계약서 템플릿"
          completed={!!template}
          onClick={() => setStep(1)}
        >
          {step === 1 && (
            <div className="space-y-2">
              {templates.length === 0 && (
                <div className="text-center text-[13px] text-[#999] py-6">
                  {allTemplates.length === 0
                    ? "등록된 양식이 없어요"
                    : "활성 양식이 없어요. 양식을 활성 상태로 바꿔야 발송할 수 있어요"}
                </div>
              )}
              {templates.map((t) => (
                <button
                  key={t.contractSeq}
                  type="button"
                  onClick={() => {
                    setTemplateSeq(t.contractSeq);
                    setStep(2);
                  }}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    templateSeq === t.contractSeq
                      ? "bg-[#FFF7E6] border-[#FF9934]"
                      : "bg-white border-[#E9E9E9] hover:border-[#FF9934]"
                  }`}
                >
                  <div className="text-[14px] font-semibold text-[#111]">
                    {t.contractName}
                  </div>
                  <div className="text-[11px] text-[#999] mt-0.5">
                    필드 {t.fieldCount}개 · 체결 {t.signedCount}건
                  </div>
                </button>
              ))}
            </div>
          )}
          {step !== 1 && template && (
            <div className="text-[13px] text-[#666]">
              ✓ {template.contractName}{" "}
              <span className="text-[#999]">
                (필드 {template.fieldCount}개)
              </span>
            </div>
          )}
        </Section>

        {/* ── 2. 받는 사람 ── */}
        <Section
          step={2}
          currentStep={step}
          title="받는 사람"
          completed={!!selectedUserSeq && !!selectedPetSeq}
          disabled={!template}
          onClick={() => template && setStep(2)}
        >
          {step === 2 && (
            <>
              <div className="mb-1 text-[11px] text-[#999]">
                회원코드 · 전화번호 · 반려동물 이름
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") runRecipientSearch(searchInput);
                  }}
                  placeholder="회원코드·전화번호·이름 입력"
                  className="min-w-0 flex-1 text-[13px] border border-[#E9E9E9] rounded-lg px-3 py-2 focus:outline-none focus:border-[#FF9934]"
                />
                <button
                  type="button"
                  onClick={() => runRecipientSearch(searchInput)}
                  className="shrink-0 rounded-lg bg-[#FF9934] px-3 py-2 text-[13px] font-semibold text-white"
                >
                  조회
                </button>
                <button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  className="shrink-0 rounded-lg border border-[#FF9934] px-3 py-2 text-[13px] font-semibold text-[#FF9934]"
                >
                  📷 QR
                </button>
              </div>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {searchLoading && (
                  <div className="text-center text-[12px] text-[#999] py-6">
                    검색 중…
                  </div>
                )}
                {!searchLoading && searchError && (
                  <div className="text-center text-[12px] text-[#999] py-6">
                    {searchError}
                  </div>
                )}
                {!searchLoading &&
                  results.map((row) => {
                    const on =
                      selectedUserSeq === row.userSeq &&
                      selectedPetSeq === row.petSeq;
                    return (
                      <div
                        key={`${row.userSeq}-${row.petSeq}`}
                        onClick={() => selectRecipient(row)}
                        className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                          on
                            ? "border-[#FF9934] bg-[#FFF7E6]"
                            : "border-[#E9E9E9] bg-white hover:border-[#FF9934]"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-[14px] font-semibold text-[#111] inline-flex items-center gap-1">
                            <IconPaw className="w-3.5 h-3.5" />
                            {row.petName || "이름 없음"}
                            {row.ourCustomer && (
                              <span className="rounded bg-[#FFF1DD] px-1.5 py-0.5 text-[10px] font-semibold text-[#D97A15]">
                                우리 매장 고객
                              </span>
                            )}
                          </div>
                          {row.existingCustomer ? (
                            <span className="rounded bg-[#E6F4EA] px-2 py-0.5 text-[10px] font-semibold text-[#1f9d57]">
                              계약완료
                            </span>
                          ) : (
                            <span className="rounded bg-[#FDECEC] px-2 py-0.5 text-[10px] font-semibold text-[#E0453E]">
                              미계약
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-[12px] text-[#666]">
                          보호자 {row.userName || "-"} ·{" "}
                          {row.phoneNumber || "-"}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
          {step !== 2 && !!selectedUserSeq && (
            <div className="text-[13px] text-[#666]">
              ✓ {selectedUserName}{" "}
              <span className="text-[#999]">/ {selectedPetName}</span>
            </div>
          )}
        </Section>

        {/* ── 3. 메시지 (선택) ── 변수는 보호자 서명 화면에서 자동 채움 */}
        <Section
          step={3}
          currentStep={step}
          title="메시지 (선택)"
          completed={false}
          disabled={step < 3}
          onClick={() => step >= 3 && setStep(3)}
        >
          {step === 3 && (
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="예: 위탁 시작 전 동의 부탁드려요!"
              rows={3}
              className="w-full text-[13px] border border-[#E9E9E9] rounded-lg p-3 focus:outline-none focus:border-[#FF9934] resize-none"
            />
          )}
        </Section>

        {/* 사업장 도장 선택 */}
        <div className="mx-4 mt-3 mb-3 bg-white rounded-xl border border-[#E9E9E9] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[13px] font-bold text-[#111] inline-flex items-center gap-1.5">
              <IconStamp className="w-4 h-4 text-[#FF9934]" />
              사용할 도장
            </div>
            <button
              type="button"
              onClick={() =>
                history.push("/business/layout/seal-management" as any)
              }
              className="text-[10px] text-[#FF9934] underline"
            >
              도장 관리
            </button>
          </div>
          {activeSeals.length === 0 ? (
            <div className="text-[12px] text-[#999] py-3 text-center bg-[#FAFAFA] rounded-lg">
              등록된 도장이 없어요. 도장 관리에서 먼저 등록해 주세요
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {activeSeals.map((s) => (
                <button
                  key={s.sealSeq}
                  type="button"
                  onClick={() =>
                    setSelectedSealSeq(
                      selectedSealSeq === s.sealSeq ? null : s.sealSeq,
                    )
                  }
                  className={`relative rounded-lg border p-2 ${
                    selectedSealSeq === s.sealSeq
                      ? "border-[#FF9934] bg-[#FFF7E6]"
                      : "border-[#E9E9E9] bg-white"
                  }`}
                >
                  <div className="aspect-square flex items-center justify-center bg-[#FAFAFA] rounded mb-1">
                    <img src={s.imageUrl} className="max-w-[80%] max-h-[80%]" />
                  </div>
                  <div className="text-[10px] truncate font-medium text-[#111]">
                    {s.sealName}
                  </div>
                  {s.defaultYn === "Y" && (
                    <span className="absolute top-1 right-1 text-[8px] bg-[#FF9934] text-white font-bold rounded px-1">
                      기본
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {selectedSealSeq && (
            <div className="text-[10px] text-[#22A55B] mt-2 text-center">
              ✓ 선택한 도장이 사업장 도장 자리에 합성돼요
            </div>
          )}
          {templateRequiresSeal && !selectedSealSeq && (
            <div className="text-[10px] text-[#D33] mt-2 text-center">
              이 계약서는 사업장 도장 선택이 필요해요
            </div>
          )}
        </div>

        {/* 발송 버튼 */}
        <div className="px-4 pt-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="w-full py-3 text-[14px] font-bold bg-[#FF9934] text-white rounded-full disabled:bg-[#E9E9E9] disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
          >
            <IconSend className="w-[18px] h-[18px]" />
            서명 요청 보내기
          </button>
          <div className="text-[10px] text-[#999] text-center mt-2">
            받는 분에게 푸시 알림과 알림장이 즉시 발송됩니다
          </div>
        </div>
      </div>

      {scannerOpen && (
        <QrScannerModal
          onClose={() => setScannerOpen(false)}
          onScan={(text) => {
            setScannerOpen(false);
            setSearchInput(text);
            runRecipientSearch(text);
          }}
        />
      )}
    </>
  );
}

// ── 보조 컴포넌트 ──────────────────────────────────────────

const Section = ({
  step,
  currentStep,
  title,
  completed,
  disabled,
  onClick,
  children,
}: {
  step: number;
  currentStep: number;
  title: string;
  completed: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  const isActive = step === currentStep;
  return (
    <div
      className={`mx-4 my-3 rounded-xl border p-4 ${
        isActive
          ? "bg-white border-[#FF9934]"
          : disabled
            ? "bg-[#FAFAFA] border-[#E9E9E9] opacity-60"
            : "bg-white border-[#E9E9E9]"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full flex items-center gap-2 mb-2 disabled:cursor-not-allowed"
      >
        <span
          className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
            completed
              ? "bg-[#22A55B] text-white"
              : isActive
                ? "bg-[#FF9934] text-white"
                : "bg-[#E9E9E9] text-[#999]"
          }`}
        >
          {completed ? "✓" : step}
        </span>
        <span className="text-[14px] font-semibold text-[#111]">{title}</span>
      </button>
      <div>{children}</div>
    </div>
  );
};
